import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Button, Switch, Card, Typography, Spin, message, Space, Divider, Tag, Tooltip, Select, Modal } from 'antd';
import { SaveOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { platformSettingsService, type PlatformSetting } from '../../../shared/api/services/platform-settings.service';

const { Title, Text } = Typography;

export const RFQSettings: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState<PlatformSetting[]>([]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await platformSettingsService.getSettings();
            if (response.success) {
                const allSettings = Object.values(response.data).flat();
                setSettings(allSettings);

                // Map settings to form values
                const formValues: Record<string, any> = {};
                allSettings.forEach(s => {
                    formValues[s.key] = s.value;
                });
                form.setFieldsValue(formValues);
            }
        } catch (error: any) {
            message.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const onFinish = async (values: any) => {
        const oldDefaultRadius = getSettingByKey('default_bidding_radius_km')?.value;
        const newDefaultRadius = values.default_bidding_radius_km;

        if (oldDefaultRadius && String(oldDefaultRadius) !== String(newDefaultRadius)) {
            Modal.warning({
                title: 'Important: Radius Pricing Bands',
                content: `You changed the default bidding radius from ${oldDefaultRadius} KM to ${newDefaultRadius} KM. Remember that pricing bands are absolute targets. You must also update the active Radius Pricing Bands to match this new base radius to ensure merchants are billed correctly.`,
                onOk: () => saveSettings(values)
            });
        } else {
            saveSettings(values);
        }
    };

    const saveSettings = async (values: any) => {
        try {
            setSaving(true);
            const response = await platformSettingsService.updateSettings(values);
            if (response.success) {
                message.success('Settings updated successfully');
            }
        } catch (error: any) {
            message.error('Failed to update settings');
        } finally {
            setSaving(false);
            fetchSettings();
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
    }

    const getSettingByKey = (key: string) => settings.find(s => s.key === key);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{}}
        >
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={4}>Global RFQ & Monetization Defaults</Title>
                    <Text type="secondary">Configure platform-wide rules for RFQ distribution and wallet checks.</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={fetchSettings}>Refresh</Button>
                    <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={saving}>
                        Save All Changes
                    </Button>
                </Space>
            </div>

            <Divider orientation="left">RFQ Distribution</Divider>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <Card
                    size="small"
                    title={
                        <Space>
                            Default Bidding Radius
                            <Tooltip title={getSettingByKey('default_bidding_radius_km')?.description || 'Loading...'}>
                                <span><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} /></span>
                            </Tooltip>
                        </Space>
                    }
                    extra={<Tag color="blue">KM</Tag>}
                >
                    <Form.Item name="default_bidding_radius_km" style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                    </Form.Item>
                </Card>

                <Card
                    size="small"
                    title={
                        <Space>
                            Default Visibility Radius
                            <Tooltip title={getSettingByKey('default_visibility_radius_km')?.description || 'Loading...'}>
                                <span><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} /></span>
                            </Tooltip>
                        </Space>
                    }
                    extra={<Tag color="blue">KM</Tag>}
                >
                    <Form.Item name="default_visibility_radius_km" style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                    </Form.Item>
                </Card>
            </div>

            <Divider orientation="left">Origin Logistics</Divider>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', marginBottom: '24px' }}>
                <Card
                    size="small"
                    title={
                        <Space>
                            Distance Calculation Engine
                            <Tooltip title={getSettingByKey('distance_engine')?.description || 'Choose the method used to calculate distance from warehouses to customers'}>
                                <span><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} /></span>
                            </Tooltip>
                        </Space>
                    }
                >
                    <Form.Item name="distance_engine" style={{ marginBottom: 0 }}>
                        <Select style={{ width: '100%', maxWidth: '400px' }}>
                            <Select.Option value="haversine">Haversine (Mathematical straight-line)</Select.Option>
                            <Select.Option value="google_maps_api">Google Maps API (Requires backend .env key)</Select.Option>
                        </Select>
                    </Form.Item>
                </Card>
            </div>

            <Divider orientation="left">Monetization & Guards</Divider>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <Card
                    size="small"
                    title={
                        <Space>
                            Default Platform Commission
                            <Tooltip title={getSettingByKey('default_commission_percentage')?.description || 'Loading...'}>
                                <span><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} /></span>
                            </Tooltip>
                        </Space>
                    }
                    extra={<Tag color="green">%</Tag>}
                >
                    <Form.Item name="default_commission_percentage" style={{ marginBottom: 0 }}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} />
                    </Form.Item>
                </Card>

                <Card
                    size="small"
                    title={
                        <Space>
                            Wallet Balance Guard
                            <Tooltip title={getSettingByKey('enable_wallet_validation')?.description || 'Loading...'}>
                                <span><InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} /></span>
                            </Tooltip>
                        </Space>
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <div style={{ flex: 1 }}>
                            <Text strong>Enable Wallet Validation</Text>
                        </div>
                        <Form.Item name="enable_wallet_validation" valuePropName="checked" noStyle>
                            <Switch />
                        </Form.Item>
                    </div>
                </Card>
            </div>

            <div style={{ marginTop: 24 }}>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={saving} block size="large">
                    Save Configuration
                </Button>
            </div>
        </Form>
    );
};

